import React, { FC } from 'react';
import ProgressIndicator from './ProgressIndicator';
import vector from './assets/coolVector3.png';
import { AlertCircle, Checkbox, CloseCircle } from 'react-ionicons';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import clsx from 'clsx';
import Loader from 'components/Loader';
import LocationIcon from 'screens/AccessExpressV3/BaseComponents/LocationIcon';
import { formatAddress, InvoiceProcessingStatus } from './utils';

const errorMessages: Record<
  Extract<
    InvoiceProcessingStatus,
    'failed-multi-site' | 'failed-multi-service' | 'failed-granite-invoice'
  >,
  string
> = {
  'failed-multi-site':
    'Quote comparisons can only be provided for a single location at this time, but you can still consolidate your invoice for billing simplicity.',
  'failed-multi-service':
    'Quote comparisons can only be provided for a single service at this time, but you can still consolidate your invoice for billing simplicity.',
  'failed-granite-invoice':
    'Granite invoices aren’t supported. Please provide a supported carrier invoice instead.',
};

interface LoadingScreenProps {
  status: InvoiceProcessingStatus;
  progress: number;
  onClose?: () => void;
  location?: {
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip_code: string;
  };
  onConfirmAddress?: () => void;
  onRejectAddress?: () => void;
  isComparing?: boolean;
  onConsolidate?: () => void;
}
const LoadingScreen: FC<LoadingScreenProps> = ({
  status,
  progress,
  onClose,
  location,
  onConfirmAddress,
  onRejectAddress,
  isComparing,
  onConsolidate,
}) => {
  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-[110px]">
        <img src={vector} className="h-full w-full opacity-50" />
      </div>
      <div
        className={clsx('mx-auto max-w-[512px] px-4 sm:px-0', {
          'py-[131px]':
            status === 'default' || status === 'address-verification',
          'py-[78px]': status === 'error',
          'py-[68px]': status === 'no-better-offer',
          'py-[70px]': [
            'failed-multi-site',
            'failed-multi-service',
            'failed-granite-invoice',
          ].includes(status),
        })}
      >
        <div className="rounded bg-[linear-gradient(90deg,_#7E77B3_1%,_#8989FF_25%,_#6C9DFF_49.9%,_#78CFDA_75%,_#9FF4A9_100%)] p-0.5 shadow-[0px_4px_4px_0px_rgba(0,_0,_0,_0.25)]">
          <div className="relative z-10 !bg-[linear-gradient(0deg,_#20202D,_#20202D)]">
            <div className="rounded bg-[linear-gradient(91.18deg,_rgba(126,119,179,0.08)_-9.83%,_rgba(137,137,255,0.08)_16.55%,_rgba(108,157,255,0.08)_43.92%,_rgba(120,207,218,0.08)_71.51%,_rgba(159,244,169,0.08)_98.99%)] p-4">
              {status === 'default' && <Default progress={progress} />}
              {status === 'error' && <Error />}
              {status === 'no-better-offer' && (
                <NoBetterOffer
                  onClose={onClose}
                  onConsolidate={onConsolidate}
                />
              )}
              {status === 'address-verification' && (
                <AddressVerification
                  location={location}
                  onConfirm={onConfirmAddress}
                  onReject={onRejectAddress}
                  isLoading={isComparing}
                />
              )}
              {[
                'failed-multi-site',
                'failed-multi-service',
                'failed-granite-invoice',
              ].includes(status) && (
                <InvoiceUploadError
                  message={errorMessages[status as keyof typeof errorMessages]}
                  onConsolidate={onConsolidate!}
                  onClose={onClose!}
                  graniteInvoice={status === 'failed-granite-invoice'}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;

const Default = ({ progress }: { progress: number }) => (
  <div className="flex flex-col gap-4 ">
    <div className="flex justify-between gap-6 text-sm font-semibold text-white">
      <p>Getting you the best quote possible.</p>
      <p>{progress}%</p>
    </div>
    <div className="w-full">
      <ProgressIndicator percentage={progress} barClassName="bg-teal-400" />
    </div>
    <p className="text-center text-xs text-content-base-subdued">
      This process could take 1-2 minutes. Please do not leave this page.
    </p>
  </div>
);

const Error = () => (
  <div className="flex flex-col items-center justify-center">
    <span className="mb-4 text-status-error-default">
      <CloseCircle width="48px" height="48px" color="currentColor" />
    </span>
    <div className="mb-7 flex flex-col items-center justify-center text-white">
      <p className="text-xl font-bold">Error processing invoice</p>
      <p className="text-center text-sm font-[700]">
        There was an issue processing your invoice. Your account team has been
        notified.
      </p>
    </div>
    <GraniteButton size="small" variant="tertiary">
      Close
    </GraniteButton>
  </div>
);

const InvoiceUploadError = ({
  message,
  onConsolidate,
  onClose,
  graniteInvoice,
}: {
  message: string;
  onConsolidate: () => void;
  onClose: () => void;
  graniteInvoice: boolean;
}) => {
  return (
    <div className="flex flex-col items-center justify-center">
      <span className="mb-4 text-status-error-default">
        <AlertCircle width="48px" height="48px" color="currentColor" />
      </span>
      <div className="mb-6 flex flex-col items-center justify-center gap-2 text-white">
        <p className="text-xl font-bold">
          Quote comparison can’t be generated.
        </p>
        <p className="text-center text-sm text-content-base-subdued">
          {message}
        </p>
      </div>

      <div className="flex gap-2">
        <GraniteButton size="small" variant="secondary" onClick={onClose}>
          Cancel
        </GraniteButton>

        <GraniteButton
          size="small"
          variant="tertiary"
          onClick={() => {
            if (graniteInvoice) {
              onClose();
            } else onConsolidate();
          }}
        >
          {graniteInvoice ? 'Upload new invoice' : 'Consolidate'}
        </GraniteButton>
      </div>
    </div>
  );
};

const NoBetterOffer = ({
  onClose,
  onConsolidate,
}: {
  onClose?: () => void;
  onConsolidate?: () => void;
}) => (
  <div className="flex flex-col items-center justify-center">
    <span className="mb-4 text-status-success-default">
      <Checkbox width="48px" height="48px" color="currentColor" />
    </span>
    <div className="mb-7 flex flex-col items-center justify-center text-white">
      <p className="text-xl font-bold">
        Your current service is already competitive
      </p>
      <p className="text-center text-sm font-[700]">
        We couldn’t beat your current rate this time. You can still rebill
        through Granite for easier management—or upload more invoices to explore
        other savings.
      </p>
    </div>
    <div className="flex gap-4">
      <GraniteButton size="small" variant="secondary" onClick={onClose}>
        Close
      </GraniteButton>
      <GraniteButton size="small" variant="tertiary" onClick={onConsolidate}>
        Consolidate
      </GraniteButton>
    </div>
  </div>
);

const AddressVerification = ({
  location,
  onConfirm,
  onReject,
  isLoading,
}: {
  location?: {
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip_code: string;
  };
  onConfirm?: () => void;
  onReject?: () => void;
  isLoading?: boolean;
}) => (
  <div className="flex flex-col items-center gap-8">
    <div className="flex flex-col items-start justify-start gap-2">
      <h1 className="ml-5 text-xl font-bold text-content-base-default">
        Is this service address correct?
      </h1>
      {location && (
        <div className="flex w-full gap-2">
          <LocationIcon width="24px" height="24px" />
          <span className="w-full text-start text-base font-bold text-content-base-subdued">
            {formatAddress(location)}
          </span>
        </div>
      )}
    </div>
    <div className="flex items-start justify-start gap-4">
      <GraniteButton
        variant="secondary"
        size="small"
        onClick={onReject}
        disabled={isLoading}
      >
        No
      </GraniteButton>
      <GraniteButton
        variant="tertiary"
        size="small"
        onClick={onConfirm}
        disabled={isLoading}
      >
        Yes
        {isLoading && <Loader animationClassname="!w-3 !h-3 ml-2" />}
      </GraniteButton>
    </div>
  </div>
);
